import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("vt_token") || null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!token) { setLoading(false); return; }
    try {
      const resp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUser(resp.data);
    } catch {
      setUser(null);
      setToken(null);
      localStorage.removeItem("vt_token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loginWithToken = useCallback((userData, sessionToken) => {
    setUser(userData);
    setToken(sessionToken);
    localStorage.setItem("vt_token", sessionToken);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
    } catch {}
    setUser(null);
    setToken(null);
    localStorage.removeItem("vt_token");
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, loginWithToken, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { API };
